/* eslint-disable no-unused-vars */
import { OverlayTrigger, Tooltip, Button } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { loaderActions } from '../../../features/Loader/LoaderSlice';
import { formatDate } from '../../../Helpers/date';
import {
  downloadPurchaseOrderLines,
  downloadTransferOrderLines
} from '../../../Helpers/xlxs/download';
import API from '../../../Services/API';
import Swal from 'sweetalert2';
import { useCurrentCompanyFullName, useCurrentCompanyID } from '../../../hooks/useCurrentCompany';
import toast from 'react-hot-toast';
import { DELIVERY_COMPLETED_FILTER } from '../../../Helpers/purchaseOrderFilters';
import { filterOrdersForVendor } from '../../../Helpers/transferOrderFilters';

function DownloadMultipleOrderLines(props) {
  const dispatch = useDispatch();
  const companyId = useCurrentCompanyID();
  const companyFullName = useCurrentCompanyFullName();
  const { isFeedBackLoopEnabled } = useSelector((state) => state.general);

  async function confirmDownload() {
    let dateFilter = '';
    if (props.dateRange.length > 0) {
      const startDate = await formatDate(props.dateRange[0]);
      const endDate = await formatDate(props.dateRange[1]);
      dateFilter = `and expectedReceiptDate gt ${startDate} and expectedReceiptDate lt ${endDate}`;
    }
    await dispatch(loaderActions.show());
    let headerAPIEndpoint = '';
    let vendorFilter = '';
    let vendorConfirmationFilter = '';
    let deliveryCompletedFilter = '';
    if (props.type === 'purchase') {
      if (props.vendorType === 'Warehouse') {
        vendorFilter = `whseVendorCode eq '${props.vendorNo}'`;
      } else {
        vendorConfirmationFilter = `and vendConfirmationSent eq true`;
        vendorFilter = `vendorNo eq '${props.vendorNo}'`;
      }
      deliveryCompletedFilter = `and ${DELIVERY_COMPLETED_FILTER}`;
      headerAPIEndpoint = '/purchase_order_header';
    } else {
      vendorFilter = `whseVendorCode eq '${props.vendorNo}'`;
      headerAPIEndpoint = '/transfer_order_header';
    }
    let vendorPortalStatusFilter = '';
    if (props.vendorPortalStatus && props.vendorPortalStatus.toLowerCase() !== 'all') {
      vendorPortalStatusFilter = `and vendorPortalStatus eq '${props.vendorPortalStatus}'`;
    }
    const params = {
      company_id: companyId,
      $filter:
        `${vendorFilter} ${deliveryCompletedFilter} ${vendorConfirmationFilter} ${dateFilter} ${vendorPortalStatusFilter}`.trim(),
      $count: true
    };
    // Purchase orders only ever need docNo here, so keep that query cheap. Transfer
    // orders also need to check filterOrdersForVendor below, and $select-ing an
    // unrecognized/undeployed field errors the whole request the same way an
    // unrecognized $filter field does - so fetch full transfer order headers instead
    // of guessing at exactly which optional fields are safe to select.
    if (props.type === 'purchase') {
      params.$select = 'docNo';
    }
    const res = await API().get(`${headerAPIEndpoint}`, { params });
    const headers = filterOrdersForVendor(res.data.value, props.type);
    const uniqueOrders = [...new Set(headers.map((line) => line.docNo))];
    if (uniqueOrders.length === 0) {
      await showError(`No ${props.type} orders available to download for ${props.vendorType} side`);
    } else {
      const { allLines, errors } = await ProcessOrderLinesDownload(uniqueOrders);
      if (errors.length > 0) {
        await showError(`An error occurred during download: ${errors}`);
      } else if (allLines.length === 0) {
        await showError(
          `No ${props.type} order lines available to download for ${props.vendorType} side (error or no data)`
        );
      } else {
        await requestLinesDownload(allLines);
      }
    }
  }

  async function ProcessOrderLinesDownload(uniqueOrders) {
    const subOrderRequestCalls = Math.ceil(uniqueOrders.length / 25);
    const batchRequestData = [];
    for (let i = 0; i < subOrderRequestCalls; i++) {
      const subOrderRequestData = uniqueOrders.slice(i * 25, (i + 1) * 25);
      const requestStructure = await getBatchURL(subOrderRequestData);
      batchRequestData.push(requestStructure);
    }
    // Collect all lines from all batches
    let allLines = [];
    let errors = [];
    if (batchRequestData.length > 5) {
      for (let i = 0; i < batchRequestData.length; i += 5) {
        const batchRequestDataChunk = batchRequestData.slice(i, i + 5);
        const result = await LoadOrderLinesBatch(batchRequestDataChunk);
        if (result.errors.length > 0) {
          // If any chunk returns no lines due to errors, stop further processing
          errors = result.errors;
          break;
        }
        allLines.push(...result.lines);
      }
    } else {
      const result = await LoadOrderLinesBatch(batchRequestData);
      allLines = result.lines;
      errors = result.errors;
    }
    return { allLines, errors };
  }

  async function showError(message) {
    await dispatch(loaderActions.hide());
    Swal.fire({
      icon: 'error',
      title: 'Oops...',
      text: message
    });
  }

  async function getBatchURL(orders) {
    let batchFunction = '';
    if (props.type === 'purchase') {
      batchFunction = 'purchaseOrderLinesVP';
    } else {
      batchFunction = 'transferOrderLinesVP';
    }
    let linesFilter = '';
    await orders.forEach((order, index) => {
      if (index === 0) {
        linesFilter = `contains(documentNo, '${order}')`;
      } else {
        linesFilter = linesFilter + ` or contains(documentNo, '${order}')`;
      }
    });
    let batchURL = `${batchFunction}?$filter=(${linesFilter})`;
    if (props.type === 'purchase' && isFeedBackLoopEnabled === true) {
      batchURL = `${batchURL} and feedbackStatus ne 'Pending' and feedbackStatus ne 'Approved'`;
    }
    batchURL = `${batchURL}&$count=true`;
    return {
      method: 'GET',
      url: batchURL,
      headers: {
        Company: companyFullName,
        'Content-Type': 'application/json',
        'If-Match': '*'
      },
      body: {}
    };
  }

  async function LoadOrderLinesBatch(batchRequestData) {
    await dispatch(loaderActions.show());
    let batchEndpoint = '';
    if (props.type === 'purchase') {
      batchEndpoint = '/purchase-batch';
    } else {
      batchEndpoint = '/transfer-batch';
    }
    const res = await API().post(batchEndpoint, { requests: batchRequestData });
    let lines = [];
    const errors = [];
    await res.data.responses.forEach((resp) => {
      if (resp.body && typeof resp.body === 'object' && resp.body.error) {
        errors.push(resp.body.error.message);
      } else if (resp.body && typeof resp.body === 'object') {
        lines = [...lines, ...resp.body.value];
      }
    });
    if (errors.length > 0) {
      await dispatch(loaderActions.hide());
      errors.forEach((err) => toast.error(err));
    }

    return { lines, errors };
  }

  async function requestLinesDownload(orderLines) {
    let dateFilter = '';
    if (props.dateRange.length > 0) {
      const startDate = formatDate(props.dateRange[0]);
      const endDate = formatDate(props.dateRange[1]);
      dateFilter = `From_${startDate}_TO_${endDate}-`;
    }
    if (orderLines.length > 0) {
      let lines = [];
      if (props.type === 'purchase') {
        if (props.vendorType.toLowerCase() === 'supplier') {
          lines = orderLines.filter((line) =>
            [
              'Confirmation Sent to Vendor',
              'Confirmed by Vendor',
              'Confirmation Sent to Warehouse'
            ].includes(line.vendorPortalStatusCaption)
          );
        } else {
          lines = orderLines;
        }
        downloadPurchaseOrderLines(
          lines,
          `${props.vendName}-${props.type}-${dateFilter}${props.vendorType.toLowerCase()}-Lines`,
          props.vendorType.toLowerCase(),
          props.vpFreightType
        );
      } else {
        downloadTransferOrderLines(
          orderLines,
          props.vendorNo,
          `${props.vendName}-TOs-${dateFilter}Lines`
        );
      }
    } else {
      toast.error('There are no valid PO lines that can be downloaded.', { id: 'download' });
    }
    await dispatch(loaderActions.hide());
  }

  return (
    <>
      <OverlayTrigger
        key="download"
        className="mr-2"
        placement={'top'}
        overlay={
          <Tooltip id={`tooltip-download`} role={'button'}>
            <strong>Mass Download</strong>
            <br />
            Download multiple order lines to Excel
          </Tooltip>
        }
      >
        <Button
          variant="outline-success"
          size="sm"
          className="download-btn"
          onClick={confirmDownload}
          style={{
            borderRadius: '6px',
            padding: '2px 4px',
            border: '1px solid #28a745',
            backgroundColor: '#f8f9fa',
            transition: 'all 0.3s ease',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px'
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = '#d4edda';
            e.target.style.borderColor = '#1e7e34';
            e.target.style.transform = 'translateY(-2px)';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = '#f8f9fa';
            e.target.style.borderColor = '#28a745';
            e.target.style.transform = 'translateY(0)';
          }}
        >
          <i className="feather icon-download" style={{ fontSize: '16px' }} />
        </Button>
      </OverlayTrigger>
    </>
  );
}

export default DownloadMultipleOrderLines;
